import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Pencil, Printer, Download, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { StatusBadge, openPdf, printPdf, downloadFile, fmtDate } from "./dpaUtils";

const TILES = [
  ["organisations", "Client organisations"], ["signed", "With signed DPA"], ["active", "Active"], ["draft", "Draft"],
  ["awaiting_signature", "Awaiting signatures"], ["requires_review", "Requiring review"], ["expired", "Expired"],
  ["superseded", "Superseded"], ["without_dpa", "Without a DPA"],
];
const COLS = [["organisation", "Organisation"], ["application_name", "Application"], ["reference", "Reference"],
  ["template_version", "Version"], ["status", "Status"], ["signed_date", "Signed"], ["effective_date", "Effective"], ["next_review_date", "Review"]];

const RowActions = ({ r }) => {
  const nav = useNavigate();
  const pdf = `/dpa/agreements/${r.id}/pdf`;
  return (
    <div className="flex gap-1">
      <Button size="icon" variant="ghost" className="h-7 w-7" title="View" onClick={() => nav(r.id ? `/compliance/agreements/${r.id}` : `/compliance/organisations/${r.organisation_id}`)} data-testid={`dpa-row-view-${r.organisation_id}`}><Eye className="h-3.5 w-3.5" /></Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit organisation" onClick={() => nav(`/compliance/organisations/${r.organisation_id}`)} data-testid={`dpa-row-edit-${r.organisation_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      {r.id && <Button size="icon" variant="ghost" className="h-7 w-7" title="Print" onClick={() => printPdf(pdf)} data-testid={`dpa-row-print-${r.organisation_id}`}><Printer className="h-3.5 w-3.5" /></Button>}
      {r.id && <Button size="icon" variant="ghost" className="h-7 w-7" title="Download PDF" onClick={() => downloadFile(`${pdf}?download=true`, `${r.reference}.pdf`)} data-testid={`dpa-row-download-${r.organisation_id}`}><Download className="h-3.5 w-3.5" /></Button>}
    </div>
  );
};

export const DpaDashboard = () => {
  const [q, setQ] = useState({ search: "", status: "all", sort: "organisation", desc: false, page: 1 });
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/dpa/overview", { params: { ...q, status: q.status === "all" ? "" : q.status, page_size: 25 } })
      .then(({ data }) => setData(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!data) return null;
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const sortBy = (k) => setQ({ ...q, sort: k, desc: q.sort === k ? !q.desc : false });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 lg:grid-cols-9 gap-2">
        {TILES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setQ({ ...q, status: { active: "active", draft: "draft", expired: "expired", without_dpa: "not_created", requires_review: "requires_review" }[k] || "all", page: 1 })}
            className="bg-white border p-3 text-left hover:border-slate-500 transition-colors" style={{ borderColor: "var(--hi-border)" }} data-testid={`dpa-count-${k}`}>
            <div className="font-heading font-black text-2xl">{data.counts[k]}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
          </button>
        ))}
      </div>
      <Card className="rounded-none border-border">
        <div className="p-4 flex gap-3 flex-wrap border-b" style={{ borderColor: "var(--hi-border)" }}>
          <Input placeholder="Search organisation or reference…" value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value, page: 1 })} className="rounded-none max-w-xs" data-testid="dpa-search-input" />
          <Select value={q.status} onValueChange={(v) => setQ({ ...q, status: v, page: 1 })}>
            <SelectTrigger className="rounded-none w-60" data-testid="dpa-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="requires_review">Requiring review</SelectItem>
              {Object.entries(data.status_labels).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dpa-org-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              {COLS.map(([k, l]) => <th key={k} className="px-4 py-2 cursor-pointer select-none" onClick={() => sortBy(k)} data-testid={`dpa-sort-${k}`}>{l} <ArrowUpDown className="inline h-3 w-3" /></th>)}
              <th className="px-4 py-2">Actions</th></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.organisation_id} className="border-t hover:bg-slate-50" style={{ borderColor: "var(--hi-border)" }} data-testid={`dpa-org-row-${r.organisation_id}`}>
                  <td className="px-4 py-2 font-medium">{r.organisation}</td>
                  <td className="px-4 py-2">{r.application_name || data.application?.name || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.reference || "—"}</td>
                  <td className="px-4 py-2">{r.template_version ? `v${r.template_version}` : "—"}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} label={r.status_label} testId={`dpa-status-${r.organisation_id}`} />{r.template_outdated && <div className="text-[10px] text-orange-700 mt-0.5">Newer template published</div>}</td>
                  <td className="px-4 py-2">{fmtDate(r.signed_date)}</td>
                  <td className="px-4 py-2">{fmtDate(r.effective_date)}</td>
                  <td className="px-4 py-2">{fmtDate(r.next_review_date)}</td>
                  <td className="px-4 py-2"><RowActions r={r} /></td>
                </tr>
              ))}
              {!data.items.length && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No organisations match.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="p-3 flex items-center justify-end gap-2 text-xs border-t" style={{ borderColor: "var(--hi-border)" }}>
          <span data-testid="dpa-pagination-info">Page {data.page} of {pages} · {data.total} organisations</span>
          <Button size="icon" variant="outline" className="h-7 w-7 rounded-none" disabled={q.page <= 1} onClick={() => setQ({ ...q, page: q.page - 1 })}><ChevronLeft className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="outline" className="h-7 w-7 rounded-none" disabled={q.page >= pages} onClick={() => setQ({ ...q, page: q.page + 1 })}><ChevronRight className="h-3.5 w-3.5" /></Button>
        </div>
      </Card>
    </div>
  );
};
